import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { get, find } from 'lodash';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { Stack } from 'office-ui-fabric-react/lib/Stack';
import { Label } from 'office-ui-fabric-react/lib/Label';
import useAPI from '../../hooks/useAPI';
import { searchByField } from '../../actions/search';
import styles from './CurrencyTypeAhead.style';
import ActionTypeaheadPicker from '../TypeaheadPicker/ActionTypeaheadPicker';
import Callout from '../Callout';

const CurrencyTypeAhead = ({
  field: { name },
  form: { errors = {}, setFieldValue = () => {}, setFieldTouched = () => {}, values = {} },
  label,
  required,
  selectedValue,
  dataTestId,
  handleCallback,
  callout,
  ...props
}) => {
  const setFormValue = value => {
    setFieldValue(name, value);
    setFieldTouched(name);
  };

  const errorMessage = get(errors, name);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const ITEMS_DISPLAYED = 5;
  const { data: suggestions, isLoading } = useAPI(
    () => searchByField('currencies', '', { start: 0, count: 220 }),
    [],
  );

  const [selectedCurrency, setSelectedCurrency] = useState(selectedValue);
  const generateDisplayName = (currencyCode, currencyName) => {
    if (currencyName) {
      return `${currencyCode} - ${currencyName}`;
    }
    return currencyCode;
  };

  const onTypeAction = async newInput => {
    if (!newInput) return;

    const searchResults = suggestions
      .filter(
        curr =>
          curr.CurrencyCode.toLowerCase().includes(newInput.toLowerCase()) ||
          curr.CurrencyName.toLowerCase()
            .replace('replaced by euro', '') // to supress the european currencies that have this suffix in master data for some reason
            .replace('rplacd by euro', '') // supressing this one, since there is a typo in the synapse master data
            .includes(newInput.toLowerCase()),
      )
      .map(curr => ({
        key: curr.CurrencyCode,
        text: generateDisplayName(curr.CurrencyCode, curr.CurrencyName),
      }))
      .sort(curr => curr.CurrencyCode)
      .slice(0, ITEMS_DISPLAYED);
    setSearchSuggestions(searchResults);
  };

  const onCurrencySelection = currency => {
    setSelectedCurrency(currency);
    setFormValue(currency.key);
    if (handleCallback) {
      handleCallback(values, errors, setFieldValue, setFieldTouched, currency, name);
    }
  };

  useEffect(() => {
    const currencyData = find(suggestions, { CurrencyCode: selectedValue.key });
    if (currencyData) {
      setSelectedCurrency({
        key: currencyData.CurrencyCode,
        text: generateDisplayName(currencyData.CurrencyCode, currencyData.CurrencyName),
      });
    }
  }, [selectedValue, suggestions]);

  return (
    <Stack>
      {label && (
        <Stack horizontal verticalAlign="start">
          <Label data-testid="typeahead-label">{label}</Label>
          {required && <Text className={styles.asterisk}>*</Text>}
          {callout && <Callout content={callout} />}
        </Stack>
      )}

      <ActionTypeaheadPicker
        options={searchSuggestions}
        selectedValue={selectedCurrency}
        onChange={onCurrencySelection}
        onTypeAction={onTypeAction}
        isLoading={isLoading}
        placeholder="Search"
        showDropdownChevron={false}
        dataTestId={dataTestId}
        aria-required={required}
        {...props}
      />

      {errorMessage && (
        <div role="alert">
          <p className={styles.errorMessage}>
            <span data-testid="typeahead-required" data-automation-id="error-message">
              {errorMessage}
            </span>
          </p>
        </div>
      )}
    </Stack>
  );
};

CurrencyTypeAhead.propTypes = {
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    onChange: PropTypes.func,
  }).isRequired,
  form: PropTypes.shape({
    errors: PropTypes.shape({}),
    setFieldTouched: PropTypes.func,
    setFieldValue: () => {},
    values: PropTypes.shape({}),
  }),
  label: PropTypes.string,
  required: PropTypes.bool,
  selectedValue: PropTypes.object,
  dataTestId: PropTypes.string,
  handleCallback: () => {},
  callout: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

CurrencyTypeAhead.defaultProps = {
  required: false,
  label: null,
  selectedValue: null,
  dataTestId: 'currency-typeahead',
  handleCallback: null,
  form: {
    errors: {},
    values: {},
    setFieldValue: () => {},
    setFieldTouched: () => {},
  },
  callout: null,
};

export default CurrencyTypeAhead;
